import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Search as SearchIcon, Loader2, ArrowRight, BookOpen, Compass, MapPin, Network } from "lucide-react";

/**
 * Search — one search bar, four bands of results.
 * Public; anonymous learners can search before signing up.  Each result
 * is a `<Link>` to the appropriate page (world · adventure · journey ·
 * chapter).  Query is echoed in the URL as `?q=` so results are shareable.
 */
const KIND_META = {
  world:     { label: "Worlds",     icon: BookOpen, tone: "text-marigold" },
  adventure: { label: "Adventures", icon: Compass,  tone: "text-teal" },
  journey:   { label: "Road-trip",  icon: MapPin,   tone: "text-marigold" },
  chapter:   { label: "Chapters",   icon: Network,  tone: "text-ink" },
};

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!q || !q.trim()) { setData(null); return; }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=6`);
        setData(data);
      } catch { setData({ groups: {}, total: 0 }); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    // Keep URL in sync for shareability
    if (q && q.trim()) setParams({ q: q.trim() }, { replace: true });
    else if (params.get("q")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const total = data?.total ?? 0;
  const isEmpty = data && total === 0 && q.trim();

  return (
    <div className="container-narrow py-12 md:py-16" data-testid="search-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-2">Search</div>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold text-ink leading-[1.02] mb-4 max-w-3xl">
          One bar for every <span className="italic text-marigold gold-underline">idea</span> in the book.
        </h1>
        <p className="text-ink2 max-w-2xl">
          Type any concept — recursion, dns, memory, machine learning — and we'll
          find the worlds, adventures, journey stops and chapters that touch it.
        </p>
      </div>

      {/* Input */}
      <div className="mb-8">
        <div className="relative rounded-2xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1F1A17] focus-within:shadow-[10px_10px_0_0_#1F1A17] focus-within:-translate-y-0.5 transition-all">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-marigold" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="try:  recursion, algorithms, dns, memory…"
            className="w-full py-4 pl-12 pr-4 text-lg bg-transparent focus:outline-none font-heading"
            data-testid="search-input"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-ink2">
          <span className="opacity-70">quick starts:</span>
          {["recursion", "algorithms", "memory", "dns", "compilers", "functions"].map((t) => (
            <button
              key={t}
              onClick={() => setQ(t)}
              className="px-2.5 py-1 rounded-full border border-ink/30 hover:border-marigold hover:text-marigold transition"
              data-testid={`search-quick-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Loading / empty / results */}
      {loading && !data && (
        <div className="py-14 text-center text-ink2" data-testid="search-loading">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        </div>
      )}

      {isEmpty && (
        <div className="py-14 text-center text-ink2" data-testid="search-empty">
          <p className="italic">Ms. Pradhan couldn't find that idea in the book yet.</p>
          <p className="text-sm mt-1">Try a related word — e.g. try <em>loops</em> if <em>iteration</em> came up empty.</p>
        </div>
      )}

      {data && total > 0 && (
        <div className="space-y-10" data-testid="search-results">
          {(["worlds", "adventures", "journey", "chapters"]).map((groupKey) => {
            const rows = data.groups?.[groupKey] || [];
            if (!rows.length) return null;
            const kind = groupKey === "worlds" ? "world" : groupKey === "adventures" ? "adventure" : groupKey === "journey" ? "journey" : "chapter";
            const meta = KIND_META[kind];
            const Icon = meta.icon;
            return (
              <section key={groupKey} data-testid={`search-group-${groupKey}`}>
                <div className={`flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.28em] font-bold ${meta.tone}`}>
                  <Icon className="w-3.5 h-3.5" /> {meta.label} <span className="text-ink2/50 tracking-normal">· {rows.length}</span>
                </div>
                <div className="grid gap-2">
                  {rows.map((r) => (
                    <Link
                      key={`${kind}-${r.slug}`}
                      to={r.link}
                      className="group flex items-start gap-3 rounded-2xl border-2 border-ink bg-cream shadow-[4px_4px_0_0_#1F1A17] hover:-translate-y-0.5 hover:shadow-[7px_7px_0_0_#1F1A17] transition-all p-4 md:p-5"
                      data-testid={`search-hit-${kind}-${r.slug}`}
                    >
                      <div className="flex-1 min-w-0">
                        {r.kicker && <div className="text-[10px] font-mono font-bold text-ink/50 mb-0.5 uppercase tracking-[0.2em]">{r.kicker}</div>}
                        <div className="font-heading text-lg font-semibold text-ink group-hover:text-marigold transition leading-snug">{r.title}</div>
                        {r.summary && <div className="text-sm text-ink2 mt-1 line-clamp-2">{r.summary}</div>}
                      </div>
                      <ArrowRight className="w-4 h-4 text-ink/40 group-hover:text-marigold group-hover:translate-x-0.5 transition mt-1 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
